import axios from 'axios';
import { Config } from '../config';

// Helper function to get axios config with authentication
const getAxiosConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'access_token': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

// Helper function for form data requests
const getFormDataConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'access_token': `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  };
};

/**
 * Offensive Operations API Service
 * Handles all backend communication for offensive security operations
 */
class OffensiveApiService {
  constructor() {
    this.baseURL = `${Config.APIBaseURL}`;
  }

  // ==================== ENGAGEMENT MANAGEMENT ====================

  /**
   * Create a new offensive engagement
   * @param {Object} engagementData - Engagement configuration
   * @param {Object} scopeData - Scope configuration
   * @param {Array} files - Uploaded files
   * @returns {Promise<Object>} Created engagement
   */
  async createEngagement(engagementData, scopeData = {}, files = []) {
    try {
      const formData = new FormData();
      formData.append('engagementData', JSON.stringify(engagementData));
      formData.append('scopeData', JSON.stringify(scopeData));
      
      // Add files
      files.forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/add/`,
        formData,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get all offensive engagements for the current user
   * Fetches all engagements and filters for offensive subtypes (1, 2, 3, 4) on the frontend
   * @returns {Promise<Array>} List of offensive engagements
   */
  async getEngagements() {
    try {
      // Fetch all engagements (backend doesn't support comma-separated subtype_id)
      const response = await axios.get(
        `${this.baseURL}/engagements/`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      
      // Filter for offensive subtypes (1: Red Team, 2: NIT, 3: DAST, 4: SAST)
      const offensiveEngagements = (response.data || []).filter(eng => 
        eng.subtype_id && [1, 2, 3, 4].includes(eng.subtype_id)
      );
      
      return offensiveEngagements;
    } catch (error) {
      console.error('Error fetching engagements:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get specific engagement by ID
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Engagement details
   */
  async getEngagement(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update engagement configuration
   * @param {string} engagementId - Engagement ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated engagement
   */
  async updateEngagement(engagementId, updates) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/update/${engagementId}`,
        updates,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<void>}
   */
  async deleteEngagement(engagementId) {
    try {
      await axios.delete(
        `${this.baseURL}/engagements/${engagementId}`,
        getAxiosConfig()
      );
    } catch (error) {
      console.error('Error deleting engagement:', error);
      throw this.handleError(error);
    }
  }

  // ==================== ENGAGEMENT CONTROL ====================

  /**
   * Start engagement execution
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Execution status
   */
  async startEngagement(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-start`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error starting engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Pause engagement execution
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Execution status
   */
  async pauseEngagement(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-pause`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error pausing engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Resume engagement execution
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Execution status
   */
  async resumeEngagement(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/resume`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error resuming engagement:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Stop engagement execution
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Execution status
   */
  async stopEngagement(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-stop`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error stopping engagement:', error);
      throw this.handleError(error);
    }
  }

  // ==================== TACTIC MANAGEMENT ====================

  /**
   * Get available tactics for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of tactics
   */
  async getTactics(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/tactics`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tactics:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update tactic status
   * @param {string} engagementId - Engagement ID
   * @param {string} tacticId - Tactic ID
   * @param {Object} updates - Status updates
   * @returns {Promise<Object>} Updated tactic
   */
  async updateTactic(engagementId, tacticId, updates) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagements/${engagementId}/tactics/${tacticId}`,
        updates,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating tactic:', error);
      throw this.handleError(error);
    }
  }

  // ==================== TECHNIQUE MANAGEMENT ====================

  /**
   * Get techniques for a specific tactic
   * @param {string} engagementId - Engagement ID
   * @param {string} tacticId - Tactic ID
   * @returns {Promise<Array>} List of techniques
   */
  async getTechniques(engagementId, tacticId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/tactics/${tacticId}/techniques`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching techniques:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Add a technique to an engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} techniqueData - Technique configuration
   * @returns {Promise<Object>} Created technique
   */
  async addTechnique(engagementId, techniqueData) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/techniques`,
        techniqueData,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error adding technique:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Execute a specific technique
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @returns {Promise<Object>} Execution status
   */
  async executeTechnique(engagementId, techniqueId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/techniques/${techniqueId}/execute`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error executing technique:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Stop technique execution
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @returns {Promise<Object>} Execution status
   */
  async stopTechnique(engagementId, techniqueId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/techniques/${techniqueId}/stop`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error stopping technique:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get technique output/logs
   * @param {string} engagementId - Engagement ID
   * @param {string} techniqueId - Technique ID
   * @returns {Promise<Object>} Technique output
   */
  async getTechniqueOutput(engagementId, techniqueId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/techniques/${techniqueId}/output`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching technique output:', error);
      throw this.handleError(error);
    }
  }

  // ==================== FINDINGS MANAGEMENT ====================

  /**
   * Get findings for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of findings
   */
  async getFindings(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/findings`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching findings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Add a finding
   * @param {string} engagementId - Engagement ID
   * @param {Object} findingData - Finding data
   * @returns {Promise<Object>} Created finding
   */
  async addFinding(engagementId, findingData) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/findings`,
        findingData,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error adding finding:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update finding
   * @param {string} engagementId - Engagement ID
   * @param {string} findingId - Finding ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated finding
   */
  async updateFinding(engagementId, findingId, updates) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagements/${engagementId}/findings/${findingId}`,
        updates,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating finding:', error);
      throw this.handleError(error);
    }
  }

  // ==================== AI CONVERSATIONAL INTERFACE ====================

  /**
   * Send message to AI assistant (streaming response)
   * @param {string} message - User message
   * @param {string} engagementId - Optional engagement ID for context
   * @returns {Promise<ReadableStream>} Streaming response
   */
  async sendMessage(message, engagementId = null) {
    try {
      const payload = {
        message,
        ...(engagementId && { engagement_id: engagementId })
      };

      const response = await fetch(`${this.baseURL}/chat`, {
        method: 'POST',
        headers: {
          'access_token': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.body;
    } catch (error) {
      console.error('Error sending message:', error);
      throw this.handleError(error);
    }
  }

  // ==================== TARGET MANAGEMENT ====================

  /**
   * Add targets to engagement
   * @param {string} engagementId - Engagement ID
   * @param {Array} targets - Array of target objects
   * @returns {Promise<Object>} Added targets
   */
  async addTargets(engagementId, targets) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/targets`,
        { targets },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error adding targets:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get targets for engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of targets
   */
  async getTargets(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/targets`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching targets:', error);
      throw this.handleError(error);
    }
  }

  // ==================== REPORTING ====================

  /**
   * Generate engagement report
   * @param {string} engagementId - Engagement ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Report data
   */
  async generateReport(engagementId, options = {}) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagements/${engagementId}/report`,
        options,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error generating report:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Download report as file
   * @param {string} engagementId - Engagement ID
   * @param {string} format - Report format (pdf, html, json)
   * @returns {Promise<Blob>} Report file
   */
  async downloadReport(engagementId, format = 'pdf') {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/report/download?format=${format}`,
        {
          ...getAxiosConfig(),
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error downloading report:', error);
      throw this.handleError(error);
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Get available tools and techniques
   * @returns {Promise<Object>} Available tools and techniques
   */
  async getAvailableTools() {
    try {
      const response = await axios.get(
        `${this.baseURL}/tools`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching available tools:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Validate target connectivity
   * @param {string} target - Target to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateTarget(target) {
    try {
      const response = await axios.post(
        `${this.baseURL}/validate-target`,
        { target },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error validating target:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get engagement statistics
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Engagement statistics
   */
  async getEngagementStats(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagements/${engagementId}/stats`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement stats:', error);
      throw this.handleError(error);
    }
  }

  // ==================== OFFENSIVE SCOPE MANAGEMENT ====================

  /**
   * Get offensive scope for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Scope details
   */
  async getOffensiveScope(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/scope`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching offensive scope:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update offensive scope for an engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} scopeUpdate - Scope update data
   * @returns {Promise<Object>} Updated scope
   */
  async updateOffensiveScope(engagementId, scopeUpdate) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/offensive/${engagementId}/scope`,
        scopeUpdate,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating offensive scope:', error);
      throw this.handleError(error);
    }
  }

  // ==================== OFFENSIVE FINDINGS ====================

  /**
   * Add a finding to an offensive engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} finding - Finding data
   * @returns {Promise<Object>} Created finding
   */
  async addOffensiveFinding(engagementId, finding) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/finding`,
        finding,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding offensive finding:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get all findings for an offensive engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of findings
   */
  async getOffensiveFindings(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/findings`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching offensive findings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update an offensive finding
   * @param {string} findingId - Finding ID
   * @param {Object} findingUpdate - Finding update data
   * @returns {Promise<Object>} Updated finding
   */
  async updateOffensiveFinding(findingId, findingUpdate) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/offensive/finding/${findingId}`,
        findingUpdate,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating offensive finding:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete an offensive finding
   * @param {string} findingId - Finding ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteOffensiveFinding(findingId) {
    try {
      const response = await axios.delete(
        `${this.baseURL}/engagement/offensive/finding/${findingId}`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting offensive finding:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete every finding on an engagement. Irreversible — the backend requires
   * confirm=true, so this must only be called behind an explicit confirmation.
   * @returns {Promise<{deleted: number, findings: Array}>}
   */
  async deleteAllOffensiveFindings(engagementId) {
    try {
      const response = await axios.delete(
        `${this.baseURL}/engagement/offensive/${engagementId}/findings?confirm=true`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting all offensive findings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Draft the Annex B risk estimation (Amenazas, Controles Mitigantes, Impacto
   * and their narratives) for findings that lack it. Imports do this
   * automatically; this is for findings added another way, or a re-draft.
   * @param {boolean} overwrite Replace estimations that already exist
   */
  async draftFindingsRiskAnalysis(engagementId, { overwrite = false } = {}) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/findings/risk-analysis/draft`,
        { overwrite },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error drafting risk analysis:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Re-attach PentAGI evidence to existing findings using their stored subtask
   * provenance. Findings imported before provenance was recorded are reported
   * under skipped_no_provenance and are unchanged.
   */
  async refreshPentAGIEvidence(engagementId, { flowIds = null } = {}) {
    try {
      const response = await axios.post(
        `${this.baseURL}/pentagi/engagement/${engagementId}/evidence/refresh`,
        flowIds ? { flow_ids: flowIds } : {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error refreshing PentAGI evidence:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Import findings from file (CherryTree .ctb/.ctd or YAML-ZIP .zip)
   * @param {string} engagementId - Engagement ID
   * @param {File} file - File to import (.ctb, .ctd, or .zip)
   * @returns {Promise<Object>} Import result with count of imported findings
   */
  async importFindings(engagementId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/findings/import`,
        formData,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error importing findings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * @deprecated Use importFindings instead. This method is kept for backward compatibility.
   */
  async importCherryTreeFindings(engagementId, cherryTreeFile) {
    return this.importFindings(engagementId, cherryTreeFile);
  }

  /**
   * Get offensive engagement progress
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Progress data
   */
  async getOffensiveProgress(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/progress`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching offensive progress:', error);
      throw this.handleError(error);
    }
  }

  // ==================== RETEST FUNCTIONALITY ====================

  /**
   * Get retest status for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Retest status with statistics and findings
   */
  async getRetestStatus(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/retest/status`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Enable retest functionality for an engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} retestConfig - Retest configuration
   * @returns {Promise<Object>} Updated retest configuration
   */
  async enableRetest(engagementId, retestConfig) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/retest/enable`,
        retestConfig,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Disable retest functionality for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} Success message
   */
  async disableRetest(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/retest/disable`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update retest configuration for an engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} retestConfig - Updated retest configuration
   * @returns {Promise<Object>} Updated retest configuration
   */
  async updateRetestConfig(engagementId, retestConfig) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/offensive/${engagementId}/retest/config`,
        retestConfig,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark a finding for retest
   * @param {string} findingId - Finding ID
   * @returns {Promise<Object>} Updated finding
   */
  async markFindingForRetest(findingId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/finding/${findingId}/retest/mark`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update retest status for a finding
   * @param {string} findingId - Finding ID
   * @param {Object} statusUpdate - Status update (status, notes)
   * @returns {Promise<Object>} Updated finding
   */
  async updateRetestStatus(findingId, statusUpdate) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/offensive/finding/${findingId}/retest/status`,
        statusUpdate,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Schedule a finding for retest
   * @param {string} findingId - Finding ID
   * @returns {Promise<Object>} Scheduled retest
   */
  async scheduleRetest(findingId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/finding/${findingId}/retest/schedule`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Complete a retest with verification result
   * @param {string} findingId - Finding ID
   * @param {Object} completion - Completion data (verified, notes)
   * @returns {Promise<Object>} Completed retest
   */
  async completeRetest(findingId, completion) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/finding/${findingId}/retest/complete`,
        completion,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get findings that require retest for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Array>} List of findings requiring retest
   */
  async getRetestFindings(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/retest/findings`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== AI ORCHESTRATION ====================

  /**
   * Start AI orchestration for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} AI orchestration status
   */
  async startAIOrchestration(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-start`,
        {},
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error starting AI orchestration:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Stop AI orchestration for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} AI orchestration status
   */
  async stopAIOrchestration(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-stop`,
        {},
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error stopping AI orchestration:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get AI orchestration status for an engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Object>} AI orchestration status
   */
  async getAIOrchestrationStatus(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/ai-status`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching AI orchestration status:', error);
      throw this.handleError(error);
    }
  }

  // ==================== ERROR HANDLING ====================

  /**
   * Handle API errors consistently
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const message = data?.message || data?.error || `Server error: ${status}`;
      return new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error: Unable to connect to server');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // ==================== CVSS UTILITIES ====================

  /**
   * Calculate CVSS score from CVSS vector
   * @param {string} cvssVector - CVSS vector string
   * @returns {Promise<Object>} Calculated CVSS score and metrics
   */
  async calculateCvssScore(cvssVector) {
    try {
      const response = await axios.post(
        `${this.baseURL}/utils/cvss/calculate-score`,
        { cvss_vector: cvssVector },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate PDF report for an offensive engagement
   * @param {string} engagementId - Engagement ID
   * @returns {Promise<Blob>} PDF file as blob
   */
  /**
   * Customers, for the dashboard's picker.
   *
   * Same endpoint Exposure's filter bar uses, so the two pickers cannot drift
   * apart on which customers exist.
   */
  async getCustomers() {
    const token = localStorage.getItem('token');
    const { data } = await axios.get(
      `${this.baseURL}/customers/`,
      { headers: { access_token: `Bearer ${token}` }, withCredentials: true },
    );
    return data;
  }

  /**
   * The findings behind one matrix cell.
   *
   * Takes the same filters the dashboard was loaded with, so the list can never
   * disagree with the count that was clicked.
   */
  async getDashboardCell(exposure, impact, customerId, scope = 'all') {
    const params = new URLSearchParams({ exposure, impact, scope });
    if (customerId) params.set('customer_id', customerId);
    const token = localStorage.getItem('token');
    const { data } = await axios.get(
      `${this.baseURL}/offensive/dashboard/cell?${params.toString()}`,
      { headers: { access_token: `Bearer ${token}` }, withCredentials: true },
    );
    return data;
  }

  /** Aggregated offensive results. One call: matrix, bands, history and table. */
  async getOffensiveDashboard(customerId, scope = 'all') {
    const params = new URLSearchParams({ scope });
    if (customerId) params.set('customer_id', customerId);
    const token = localStorage.getItem('token');
    const { data } = await axios.get(
      `${this.baseURL}/offensive/dashboard?${params.toString()}`,
      { headers: { access_token: `Bearer ${token}` }, withCredentials: true },
    );
    return data;
  }

  async generateReport(engagementId) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/generate-report`,
        {},
        {
          headers: {
            'access_token': `Bearer ${token}`,
          },
          withCredentials: true,
          responseType: 'blob', // Important: receive binary data
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating report:', error);
      
      // Handle blob error responses (when responseType is 'blob', errors are also blobs)
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          const errorMessage = new Error(errorData.detail || errorData.message || 'Error al generar el reporte');
          errorMessage.response = { data: errorData };
          throw errorMessage;
        } catch (parseError) {
          // If parsing fails, return generic error
          throw new Error('Error al generar el reporte. Por favor, intente nuevamente.');
        }
      }
      
      throw this.handleError(error);
    }
  }

  // ==================== REPORT CONFIGURATION ====================

  async getReportConfig(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/engagement/offensive/${engagementId}/report-config`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateReportConfig(engagementId, config) {
    try {
      const response = await axios.put(
        `${this.baseURL}/engagement/offensive/${engagementId}/report-config`,
        config,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async autoPopulateReportConfig(engagementId) {
    try {
      const response = await axios.post(
        `${this.baseURL}/engagement/offensive/${engagementId}/report-config/auto-populate`,
        {},
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PENTAGI INTEGRATION ====================

  async sendFlowMessage(flowId, message) {
    try {
      const response = await axios.post(
        `${this.baseURL}/pentagi/flow/${flowId}/message`,
        { message },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async importPentAGIResults(engagementId, { language = 'en' } = {}) {
    try {
      const response = await axios.post(
        `${this.baseURL}/pentagi/import/${engagementId}`,
        { language },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getImportStatus(engagementId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/pentagi/import/${engagementId}/status`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== MITRE ATT&CK KNOWLEDGE BASE ====================

  /**
   * Get all MITRE ATT&CK tactics
   * @returns {Promise<Array>} List of tactics
   */
  async getMitreTactics() {
    try {
      const response = await axios.get(
        `${this.baseURL}/mitre/tactics`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE tactics:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific MITRE ATT&CK tactic
   * @param {string} tacticId - Tactic ID (e.g., 'TA0004')
   * @returns {Promise<Object>} Tactic details
   */
  async getMitreTactic(tacticId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/mitre/tactics/${tacticId}`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE tactic:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get MITRE ATT&CK techniques
   * @param {string} tacticId - Optional tactic ID to filter by
   * @returns {Promise<Array>} List of techniques
   */
  async getMitreTechniques(tacticId = null) {
    try {
      const url = tacticId 
        ? `${this.baseURL}/mitre/techniques?tactic=${tacticId}`
        : `${this.baseURL}/mitre/techniques`;
      const response = await axios.get(url, getAxiosConfig());
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE techniques:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific MITRE ATT&CK technique
   * @param {string} techniqueId - Technique ID (e.g., 'T1134')
   * @returns {Promise<Object>} Technique details
   */
  async getMitreTechnique(techniqueId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/mitre/techniques/${techniqueId}`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE technique:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get MITRE ATT&CK subtechniques
   * @param {string} parentTechniqueId - Optional parent technique ID to filter by
   * @returns {Promise<Array>} List of subtechniques
   */
  async getMitreSubtechniques(parentTechniqueId = null) {
    try {
      const url = parentTechniqueId
        ? `${this.baseURL}/mitre/subtechniques?parent_technique=${parentTechniqueId}`
        : `${this.baseURL}/mitre/subtechniques`;
      const response = await axios.get(url, getAxiosConfig());
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE subtechniques:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get MITRE ATT&CK tools
   * @returns {Promise<Array>} List of tools
   */
  async getMitreTools() {
    try {
      const response = await axios.get(
        `${this.baseURL}/mitre/tools`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching MITRE tools:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Search MITRE ATT&CK knowledge base
   * @param {string} term - Search term
   * @param {string} itemType - Optional item type filter ('tactic', 'technique', 'subtechnique', 'tool')
   * @returns {Promise<Array>} Search results
   */
  async searchMitre(term, itemType = null) {
    try {
      const url = itemType
        ? `${this.baseURL}/mitre/search/${encodeURIComponent(term)}?item_type=${itemType}`
        : `${this.baseURL}/mitre/search/${encodeURIComponent(term)}`;
      const response = await axios.get(url, getAxiosConfig());
      return response.data;
    } catch (error) {
      console.error('Error searching MITRE knowledge base:', error);
      throw this.handleError(error);
    }
  }

  // ==================== VILLAGER EXPLOITS ====================

  /**
   * Get Villager exploits (paginated)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 50)
   * @param {string} severity - Optional severity filter ('CRITICAL', 'HIGH', 'MEDIUM', 'LOW')
   * @returns {Promise<Array>} List of exploits
   */
  async getVillagerExploits(page = 1, limit = 50, severity = null) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      if (severity) {
        params.append('severity', severity);
      }
      const response = await axios.get(
        `${this.baseURL}/villager/exploits?${params.toString()}`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Villager exploits:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Search Villager exploits
   * @param {string} term - Search term
   * @param {number} limit - Maximum results (default: 50)
   * @returns {Promise<Array>} Search results
   */
  async searchVillagerExploits(term, limit = 50) {
    try {
      const response = await axios.get(
        `${this.baseURL}/villager/exploits/search/${encodeURIComponent(term)}?limit=${limit}`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error searching Villager exploits:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get Villager exploits statistics
   * @returns {Promise<Object>} Statistics object
   */
  async getVillagerExploitsStats() {
    try {
      const response = await axios.get(
        `${this.baseURL}/villager/exploits/stats`,
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Villager exploits stats:', error);
      throw this.handleError(error);
    }
  }
}

// Create and export singleton instance
const offensiveApi = new OffensiveApiService();
export default offensiveApi;
